import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  UserGroupIcon,
  DotsCircleHorizontalIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PencilIcon,
} from '@heroicons/react/solid';
import { useTranslation } from 'react-i18next';

const propTypes = {
  id: PropTypes.string.isRequired,
  joined: PropTypes.bool.isRequired,
  lessons: PropTypes.number.isRequired,
  position: PropTypes.number.isRequired,
  image: PropTypes.string.isRequired,
  members: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  updateCourse: PropTypes.func.isRequired,
  onClickEdit: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool.isRequired,
};

type componentProps = PropTypes.InferProps<typeof propTypes>;

function CourseCard(props: componentProps) {
  const {
    id,
    lessons,
    image,
    members,
    title,
    description,
    position,
    isAdmin,
    updateCourse,
    onClickEdit,
  } = props;

  const { t } = useTranslation();

  const updatePosition = (amount: number) =>
    updateCourse({ _id: id, position: position + amount });

  return (
    <div key={id} className="w-full bg-white shadow-md rounded-md p-2">
      <Link to={`${id}`}>
        {/* Image area always reserves 250px so card heights stay uniform.
            When `image` is empty/falsy, render a neutral placeholder div
            instead of an <img src="">, which otherwise shows the broken-
            image icon + alt text ("topic banner") on courses that don't
            have a banner uploaded. */}
        {image ? (
          <img
            src={image}
            alt="course banner"
            className="object-cover w-full rounded-md mx-auto"
            style={{ height: '250px' }}
          />
        ) : (
          <div
            className="w-full rounded-md mx-auto bg-gradient-to-br from-purple-200 to-purple-400 flex items-center justify-center"
            style={{ height: '250px' }}
            aria-label="course banner placeholder"
          >
            <span className="text-white text-3xl font-bold opacity-70">
              {(title || '?').charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <section className="flex-col space-y-3 m-3">
          <div className="border-b-2 border-grey-light">
            <h1 className="text-lg font-bold text-grey-medium truncate">
              {title}
            </h1>
            <h3 className="hidden lg:block truncate text-md text-grey-medium_light">
              {description}
            </h3>
          </div>
          <div className="flex-col space-y-3">
            <div className="flex-col">
              <div className="flex space-x-3">
                <UserGroupIcon className="h-5 w-4" />
                <p className="flex space-x-1">
                  <span>{members}</span>
                  <span>{t('courses.members')}</span>
                </p>
              </div>
              <div className="flex space-x-3">
                <DotsCircleHorizontalIcon className="h-5 w-4" />
                <p className="flex space-x-1">
                  <span>{lessons}</span>
                  <span>{t('modules')}</span>
                </p>
              </div>
            </div>
            <div className="flex flex-col space-y-2">
              {isAdmin && (
                <div className="flex justify-between space-x-3">
                  <div className="flex space-x-1">
                    <button
                      disabled={position <= 0}
                      className={`${
                        position <= 0
                          ? 'bg-grey-medium'
                          : 'bg-sky hover:bg-sky-dark'
                      }  text-white rounded-md shadow-md hover:shadow-lg p-2`}
                      onClick={(e) => {
                        e.preventDefault();
                        updatePosition(-1);
                      }}
                    >
                      <ArrowUpIcon className="w-4" />
                    </button>
                    <button
                      className="bg-sky hover:bg-sky-dark text-white rounded-md shadow-md hover:shadow-lg p-2"
                      onClick={(e) => {
                        e.preventDefault();
                        updatePosition(1);
                      }}
                    >
                      <ArrowDownIcon className="w-4" />
                    </button>
                  </div>
                  <button
                    className="bg-sky hover:bg-sky-dark text-white rounded-md shadow-md hover:shadow-lg p-2"
                    onClick={(e) => {
                      e.preventDefault();
                      onClickEdit();
                    }}
                  >
                    <PencilIcon className="w-4" />
                  </button>
                </div>
              )}
              <button className="bg-sky hover:bg-sky-dark text-white rounded-md shadow-md hover:shadow-lg p-2 w-full">
                {t('courses.button_see_more')}
              </button>
            </div>
          </div>
        </section>
      </Link>
    </div>
  );
}

CourseCard.propTypes = propTypes;

export default CourseCard;
